<template>
  <div class="tactics-page">
    <MainNavbar />
    
    <!-- Toast Notifications -->
    <ToastContainer :toasts="toasts" @remove="removeToast" />
    
    <main class="tactics-main">
      <div v-if="usePreviewData" class="tactics-preview-banner" role="status">
        <i class="fa-solid fa-flask" aria-hidden="true" />
        Preview mode — dummy match and roster (appearance only). Saving is simulated.
      </div>
      <div class="tactics-container">
        <!-- Header -->
        <div class="tactics-header">
          <div class="header-info">
            <button class="btn-back" @click="$router.back()">
              <i class="fa-solid fa-arrow-left"></i>
              Back
            </button>
            <div class="match-info">
              <h1>Tactics &amp; Mentality</h1>
              <div class="match-details">
                <span class="match-label">Match:</span>
                <span class="match-value">{{ matchInfo.teamName }} vs {{ matchInfo.opponentName }}</span>
                <span class="match-date">
                  <i class="fa-solid fa-calendar"></i>
                  {{ formatDate(matchInfo.matchDate) }}
                </span>
              </div>
            </div>
          </div>
          <div class="header-actions">
            <button class="btn-action btn-secondary" @click="resetFormation">
              <i class="fa-solid fa-undo"></i>
              Reset
            </button>
            <button class="btn-action btn-primary" @click="saveTactics">
              <i class="fa-solid fa-save"></i>
              Save Tactics
            </button>
          </div>
        </div>

        <div class="tactics-content">
          <!-- Left Sidebar - Formation Selector -->
          <div class="formation-sidebar">
            <div class="sidebar-section">
              <h3>
                <i class="fa-solid fa-users"></i>
                Game format
              </h3>
              <div class="match-type-selector">
                <button
                  v-for="type in matchTypes"
                  :key="type.value"
                  class="type-btn"
                  :class="{ active: selectedMatchType === type.value, disabled: isMatchTypeLocked }"
                  :disabled="isMatchTypeLocked"
                  @click="!isMatchTypeLocked && (selectedMatchType = type.value)"
                >
                  {{ type.label }}
                </button>
              </div>
              <p v-if="isMatchTypeLocked" class="match-type-note">
                <i class="fa-solid fa-info-circle"></i>
                Game format is set from the scheduled match
              </p>
            </div>

            <div class="sidebar-section">
              <h3>
                <i class="fa-solid fa-chess-board"></i>
                Mentality
              </h3>
              <div class="category-tabs">
                <button
                  v-for="category in formationCategories"
                  :key="category.id"
                  class="category-tab"
                  :class="{ active: selectedCategory === category.id }"
                  @click="selectedCategory = category.id"
                >
                  <i :class="category.icon"></i>
                  {{ category.label }}
                </button>
              </div>
            </div>

            <div class="sidebar-section">
              <h3>
                <i class="fa-solid fa-list"></i>
                Sets &amp; looks
              </h3>
              <div class="formations-list">
                <button
                  v-for="formation in availableFormations"
                  :key="formation"
                  class="formation-btn"
                  :class="{ active: selectedFormation === formation }"
                  @click="selectFormation(formation)"
                >
                  {{ formation }}
                </button>
              </div>
            </div>

          </div>

          <!-- Center - Pitch -->
          <div class="pitch-container">
            <div class="pitch-wrapper">
              <div class="basketball-court" ref="pitchRef">
                <!-- Court image background -->
                <img
                  src="@/assets/images/football_pitch/basketballcourt.png"
                  alt="Basketball court"
                  class="pitch-image"
                  decoding="async"
                />

                <!-- Player Positions -->
                <div
                  v-for="(position, index) in playerPositions"
                  :key="index"
                  class="player-position"
                  :class="{ 'center-position': position.role === 'center' }"
                  :style="getPositionStyle(position)"
                  @click="handlePositionClick(position)"
                  @drop="handleDrop($event, position)"
                  @dragover.prevent
                  @dragenter.prevent
                >
                  <div
                    v-if="position.player"
                    class="player-marker"
                    :class="[
                      position.role,
                      position.player.position,
                      { selected: selectedStartingPlayer && selectedStartingPlayer === position },
                    ]"
                    draggable="true"
                    @dragstart="handleDragStart($event, position)"
                    @dragend="handleDragEnd"
                    @click.stop="handlePositionClick(position)"
                  >
                    <div class="player-number">{{ getPlayerNumber(position.player) }}</div>
                    <div class="player-name-tag">{{ position.player.name }}</div>
                    <button class="remove-player" @click.stop="removePlayerFromPosition(position)">
                      <i class="fa-solid fa-times"></i>
                    </button>
                  </div>
                  <div v-else class="empty-position">
                    <i class="fa-solid fa-plus"></i>
                  </div>
                </div>
              </div>
            </div>

            <!-- Formation Display -->
            <div class="formation-display">
              <span class="formation-label">Current Formation:</span>
              <span class="formation-value">{{ selectedFormation || 'None Selected' }}</span>
            </div>
          </div>

          <!-- Right Sidebar - Substitutions -->
          <div class="tactics-sidebar">
            <!-- Substitutions Section -->
            <div class="sidebar-section substitutions-section">
              <div class="section-header-with-count">
                <h3>
                  <i class="fa-solid fa-arrows-rotate"></i>
                  Substitutions & Bench
                </h3>
                <span class="subs-count">
                  {{ benchPlayers.length }} / {{ maxSubstitutions }}
                </span>
              </div>
              <div class="substitutions-info">
                <p class="subs-note">
                  <i class="fa-solid fa-info-circle"></i>
                  Click a starter on the court, then swap with a bench player
                </p>
              </div>
              <div class="bench-players">
                <div
                  v-for="player in benchPlayers"
                  :key="player.id"
                  class="bench-player-item"
                  @click="selectBenchPlayer(player)"
                  :class="{ 'selected': selectedBenchPlayer && selectedBenchPlayer.id === player.id }"
                >
                  <div class="bench-player-avatar">
                    <i class="fa-solid fa-user"></i>
                  </div>
                  <div class="bench-player-info">
                    <div class="bench-player-name">{{ player.name }}</div>
                    <div class="bench-player-details">
                      <span class="bench-player-position">{{ player.position }}</span>
                      <span class="bench-player-number">#{{ player.number }}</span>
                    </div>
                  </div>
                  <button 
                    class="swap-btn"
                    @click.stop="swapWithStartingPlayer(player)"
                    :disabled="!selectedStartingPlayer"
                    :title="selectedStartingPlayer ? `Swap with ${selectedStartingPlayer.player?.name || 'selected player'}` : 'Select a starting player first'"
                  >
                    <i class="fa-solid fa-arrow-right-arrow-left"></i>
                  </button>
                </div>
                <div v-if="benchPlayers.length === 0" class="empty-bench">
                  <i class="fa-solid fa-users-slash"></i>
                  <p>All players are in starting lineup</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from "@/config/api";
import {
  BASKETBALL_FORMATIONS,
  BASKETBALL_FORMATION_CATEGORIES,
  BASKETBALL_GAME_FORMATS,
  getBasketballFormationLayout,
} from "@/routes/match_hub/basketball/tacticsConstants";
import {
  MOCK_BASKETBALL_PLAYERS,
  MOCK_MATCH_ID,
  MOCK_MATCH_INFO,
  MOCK_TEAM_ID,
  shouldUseTacticsPreview,
} from "@/routes/match_hub/basketball/tacticsMockData";

export default {
  name: "BasketballTacticsAndFormations",
  mixins: [toastMixin, globalLoadingMixin],
  components: {
    MainNavbar,
    ToastContainer
  },
  data() {
    return {
      loading: false,
      loadingMessage: 'Loading match data...',
      selectedMatchType: "5v5",
      selectedCategory: "balanced",
      selectedFormation: null,
      draggedPlayer: null,
      selectedStartingPlayer: null,
      selectedBenchPlayer: null,
      playerPositions: [],
      isMatchTypeLocked: false, // Lock match type based on match format
      matchInfo: {
        teamName: "",
        opponentName: "",
        matchDate: null
      },
      match: null,
      teamId: null,
      matchId: null,
      availablePlayers: [],
      matchTypes: BASKETBALL_GAME_FORMATS,
      formationCategories: BASKETBALL_FORMATION_CATEGORIES,

    };
  },
  computed: {
    usePreviewData() {
      return shouldUseTacticsPreview(this.$route);
    },
    availableFormations() {
      const matchType = BASKETBALL_FORMATIONS[this.selectedMatchType];
      if (!matchType || !matchType.categories[this.selectedCategory]) {
        return [];
      }
      return matchType.categories[this.selectedCategory];
    },
    currentFormationData() {
      return BASKETBALL_FORMATIONS[this.selectedMatchType];
    },
    startingLineup() {
      return this.playerPositions.filter(pos => pos.player !== null).map(pos => pos.player);
    },
    benchPlayers() {
      const startingIds = this.startingLineup.map(p => p.id);
      return this.availablePlayers.filter(player => !startingIds.includes(player.id));
    },
    maxSubstitutions() {
      const rules = {
        "5v5": 7,
        "3v3": 2,
      };
      return rules[this.selectedMatchType] || 5;
    },
  },
  watch: {
    selectedMatchType() {
      if (!this.isMatchTypeLocked) {
        this.resetFormation();
      }
    },
    selectedFormation(newFormation) {
      if (newFormation) {
        this.applyFormation(newFormation);
      }
    }
  },
  async mounted() {
    if (this.usePreviewData) {
      this.initPreviewData();
      return;
    }

    this.teamId = this.$route.params.team_id;
    this.matchId = this.$route.params.match_id;

    if (!this.teamId || !this.matchId) {
      console.error("Missing team_id or match_id in route params");
      this.$router.push("/ipromiseafrica/matchup/basketball/dashboard");
      return;
    }

    await this.loadMatchData();
  },
  methods: {
    initPreviewData() {
      this.teamId = MOCK_TEAM_ID;
      this.matchId = MOCK_MATCH_ID;
      this.matchInfo = { ...MOCK_MATCH_INFO };
      this.availablePlayers = MOCK_BASKETBALL_PLAYERS.map((p) => ({ ...p }));
      this.selectedMatchType = "5v5";
      this.selectedCategory = "balanced";
      this.isMatchTypeLocked = true;
      this.selectedFormation = BASKETBALL_FORMATIONS["5v5"].categories.balanced[0];
    },
    getAuthToken() {
      return (
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('token')
      );
    },
    
    /**
     * Map match_format (e.g., "11v11", "7v7", "6v6") to match type key
     */
    matchFormatToType(matchFormat) {
      if (!matchFormat) return "5v5";
      const normalized = String(matchFormat).toLowerCase().replace(/\s/g, "");
      if (normalized.includes("3")) return "3v3";
      return "5v5";
    },
    
    /**
     * Fetch match data and determine match type
     */
    async loadMatchData() {
      this.loading = true;
      this.loadingMessage = 'Loading match data...';
      
      try {
        const token = this.getAuthToken();
        if (!token) {
          throw new Error('Authentication required');
        }
        
        // Fetch team matches to find the specific match
        const response = await fetch(buildApiUrl(`/api/matches/basketball/teams/${this.teamId}/matches`), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch matches: ${response.status}`);
        }
        
        const data = await response.json();
        const matches = data.matches || [];
        
        // Find the specific match
        this.match = matches.find(m => 
          String(m.match_id || m.id) === String(this.matchId)
        );
        
        if (!this.match) {
          throw new Error('Match not found');
        }
        
        // Set match info
        this.matchInfo = {
          teamName: this.match.team_id ? 'Your Team' : 'Unknown Team',
          opponentName: this.match.opponent_name || 'TBD',
          matchDate: this.match.date || this.match.scheduled_at
        };
        
        // Determine match type from match_format
        if (this.match.match_format) {
          const matchType = this.matchFormatToType(this.match.match_format);
          this.selectedMatchType = matchType;
          this.isMatchTypeLocked = true; // Lock the match type selector
        }
        
        // Fetch team players for this match type
        await this.loadTeamPlayers();
        
        // Load existing tactics if any
        await this.loadExistingTactics();
        
        // Initialize with default formation if available
        if (this.availableFormations.length > 0 && !this.selectedFormation) {
          this.selectedFormation = this.availableFormations[0];
        }
        
      } catch (error) {
        console.error('Error loading match data:', error);
        
        // Show user-friendly error message
        let errorMessage = 'Unable to load match information. ';
        if (error.message.includes('not found')) {
          errorMessage += 'The match may have been deleted or you may not have access to it.';
        } else if (error.message.includes('Authentication')) {
          errorMessage += 'Please log in again.';
        } else {
          errorMessage += error.message || 'Please try again later.';
        }
        
        this.showToast('error', 'Failed to Load Match', errorMessage);
        
        // Redirect after a short delay to allow user to see the error
        setTimeout(() => {
          this.$router.push("/ipromiseafrica/matchup/basketball/dashboard");
        }, 2000);
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Fetch team players for the current match type
     */
    async loadTeamPlayers() {
      try {
        const token = this.getAuthToken();
        if (!token) return;
        
        // Map match type to API query format
        const response = await fetch(
          buildApiUrl(
            `/api/teams/basketball/my-team/players?game_format=${encodeURIComponent(this.selectedMatchType)}`,
          ),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!response.ok) {
          console.warn('Failed to fetch team players, using empty list');
          this.availablePlayers = [];
          return;
        }
        
        const data = await response.json();
        const players = data.players || [];
        
        // Map players to component format
        // IMPORTANT: Use team_member_number as it's the primary key in the database
        // The API returns team_member_number, so prioritize that
        this.availablePlayers = players
          .map((player, index) => {
            // team_member_number is the primary key - must use this for database foreign key
            const playerId = player.team_member_number || player.member_id || player.id;
            if (!playerId) {
              console.warn('Player missing team_member_number:', player);
              return null;
            }
            return {
              id: playerId, // This will be team_member_number
              name: player.player_name || player.name || `Player ${index + 1}`,
              position: player.position || 'Player',
              number: player.number || index + 1
            };
          })
          .filter(player => player !== null); // Filter out players without valid IDs
        
      } catch (error) {
        console.error('Error loading team players:', error);
        this.availablePlayers = [];
      }
    },
    
    /**
     * Load existing tactics if they exist
     */
    async loadExistingTactics() {
      try {
        const token = this.getAuthToken();
        if (!token) return;
        
        const response = await fetch(
          buildApiUrl(`/api/basketball/tactics/matches/${this.matchId}/teams/${this.teamId}`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!response.ok || response.status === 404) {
          // No existing tactics, start fresh
          return;
        }
        
        const data = await response.json();
        const tactics = data.tactics;
        
        if (!tactics || !tactics.player_positions) {
          return;
        }
        
        // Load existing tactics
        this.selectedFormation = tactics.formation_name;
        this.selectedCategory = tactics.formation_category || 'balanced';
        
        // Map player positions
        this.playerPositions = tactics.player_positions.map(pos => {
          const player = this.availablePlayers.find(p => p.id === pos.player_id);
          return {
            x: parseFloat(pos.position_x),
            y: parseFloat(pos.position_y),
            role: pos.role,
            player: player || null
          };
        });
        
      } catch (error) {
        console.error('Error loading existing tactics:', error);
        // Continue without existing tactics
      }
    },
    
    selectFormation(formation) {
      this.selectedFormation = formation;
    },
    applyFormation(formation) {
      const layout = getBasketballFormationLayout(this.selectedMatchType, formation);
      let playerIndex = 0;
      this.playerPositions = layout.map((spot) => ({
        x: spot.x,
        y: spot.y,
        role: spot.role,
        player: this.availablePlayers[playerIndex++] || null,
      }));
    },
    getPositionStyle(position) {
      return {
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)'
      };
    },
    handlePositionClick(position) {
      if (position.player) {
        // Select starting player for swapping
        this.selectedStartingPlayer = position;
        this.selectedBenchPlayer = null;
      }
    },
    handleDragStart(event, position) {
      this.draggedPlayer = position;
      event.dataTransfer.effectAllowed = 'move';
    },
    handleDragEnd() {
      this.draggedPlayer = null;
    },
    handleDrop(event, targetPosition) {
      if (this.draggedPlayer && this.draggedPlayer !== targetPosition) {
        // Swap players
        const temp = targetPosition.player;
        targetPosition.player = this.draggedPlayer.player;
        this.draggedPlayer.player = temp;
      }
    },
    selectBenchPlayer(player) {
      this.selectedBenchPlayer = player;
      this.selectedPlayer = null;
    },
    swapWithStartingPlayer(benchPlayer) {
      if (!this.selectedStartingPlayer || !this.selectedStartingPlayer.player) {
        this.showToast('info', 'Select Player', 'Please select a starting player to swap with');
        return;
      }
      
      // Swap the players
      const startingPlayer = this.selectedStartingPlayer.player;
      this.selectedStartingPlayer.player = { ...benchPlayer };
      
      // Clear selections
      this.selectedStartingPlayer = null;
      this.selectedBenchPlayer = null;
      
      this.showToast('success', 'Swapped', `${startingPlayer.name} ↔ ${benchPlayer.name}`);
    },

    removePlayerFromPosition(position) {
      position.player = null;
    },
    getPlayerNumber(player) {
      return player.number || '?';
    },
    resetFormation() {
      this.playerPositions = [];
      this.selectedFormation = null;
      if (this.availableFormations.length > 0) {
        this.selectedFormation = this.availableFormations[0];
      }
    },
    async saveTactics() {
      if (this.usePreviewData) {
        if (!this.selectedFormation) {
          this.showToast("warning", "No set selected", "Pick a set before saving.");
          return;
        }
        const assigned = this.playerPositions.filter((pos) => pos.player).length;
        if (!assigned) {
          this.showToast("warning", "No players", "Assign at least one player to the court.");
          return;
        }
        this.showToast(
          "success",
          "Preview saved",
          `${this.selectedFormation} with ${assigned} players (dummy — not sent to server).`,
        );
        return;
      }

      if (!this.matchId || !this.teamId) {
        this.showToast(
          'error',
          'Missing Information',
          'Match or team information is missing. Please try refreshing the page.'
        );
        return;
      }
      
      if (!this.selectedFormation) {
        this.showToast(
          'warning',
          'No Formation Selected',
          'Please select a formation before saving your tactics.'
        );
        return;
      }
      
      // Check if there are any players assigned
      const playersAssigned = this.playerPositions.filter(pos => pos.player !== null).length;
      if (playersAssigned === 0) {
        this.showToast(
          'warning',
          'No Players Assigned',
          'Please assign at least one player to a position before saving.'
        );
        return;
      }
      
      try {
        const token = this.getAuthToken();
        if (!token) {
          this.showToast(
            'error',
            'Authentication Required',
            'Please log in again to save your tactics.'
          );
          return;
        }
        
        // Show saving toast
        this.showToast(
          'info',
          'Saving Tactics',
          'Please wait while we save your formation and player positions...'
        );
        
        // Prepare player positions data
        const playerPositions = this.playerPositions.map((pos, index) => {
          const playerId = pos.player ? pos.player.id : null;
          return {
            x: pos.x,
            y: pos.y,
            role: pos.role,
            player_id: playerId,
            position_order: index
          };
        });
        
        // Prepare tactics payload
        const tacticsPayload = {
          match_id: parseInt(this.matchId),
          team_id: parseInt(this.teamId),
          match_type: this.selectedMatchType,
          formation_name: this.selectedFormation,
          formation_category: this.selectedCategory,
          player_positions: playerPositions
        };
        
        const response = await fetch(
          buildApiUrl(`/api/basketball/tactics/matches/${this.matchId}/teams/${this.teamId}`),
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(tacticsPayload)
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.detail || errorData.message || `Server error (${response.status})`;
          
          // Provide user-friendly error messages
          let userMessage = 'Failed to save tactics. ';
          if (errorMessage.includes('not found')) {
            userMessage += 'One or more players may have been removed from the team.';
          } else if (errorMessage.includes('belongs to a different team')) {
            userMessage += 'One or more players belong to a different team.';
          } else if (errorMessage.includes('validation')) {
            userMessage += 'Please check that all positions are valid.';
          } else {
            userMessage += errorMessage;
          }
          
          this.showToast('error', 'Save Failed', userMessage);
          return;
        }
        
        await response.json();

        // Success toast with helpful message
        this.showToast(
          'success',
          'Tactics Saved!',
          `Your ${this.selectedFormation} formation has been saved successfully. You can continue editing or navigate away.`
        );
        
      } catch (error) {
        console.error('Error saving tactics:', error);
        
        // User-friendly error message
        let errorMessage = 'An unexpected error occurred while saving. ';
        if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
          errorMessage += 'Please check your internet connection and try again.';
        } else {
          errorMessage += error.message || 'Please try again later.';
        }
        
        this.showToast('error', 'Save Error', errorMessage);
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
  }
};
</script>

<style scoped>
.tactics-preview-banner {
  max-width: 1400px;
  margin: 0 auto 12px;
  padding: 10px 16px;
  border-radius: 10px;
  background: linear-gradient(90deg, #fef3c7, #fde68a);
  color: #92400e;
  font-size: 0.88rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #f59e0b;
}

.tactics-page {
  background: var(--dashboard-bg);
  min-height: 100vh;
  padding-top: 120px;
  position: relative;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-spinner {
  text-align: center;
  color: white;
}

.loading-spinner i {
  font-size: 3rem;
  margin-bottom: 20px;
  color: var(--action-primary-color);
}

.loading-spinner p {
  font-size: 1.1rem;
  margin-top: 10px;
}

.tactics-main {
  max-width: 1800px;
  margin: 0 auto;
  padding: 20px;
}

.tactics-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.tactics-header {
  background: var(--card-bg);
  padding: 25px 30px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

@media (max-width: 768px) {
  .tactics-header {
    padding: 20px;
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-info {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .header-actions {
    width: 100%;
    flex-direction: column;
  }
  
  .btn-action {
    width: 100%;
  }
}

.header-info {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
}

.btn-back {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--subtext-color);
  padding: 10px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

.match-info h1 {
  margin: 0 0 10px 0;
  font-size: clamp(1.3rem, 4vw, 1.8rem);
  color: var(--heading-color);
  background: var(--name-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.match-details {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.match-label {
  color: var(--subtext-color);
  font-weight: 600;
}

.match-value {
  color: var(--heading-color);
  font-weight: 700;
}

.match-date {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--subtext-color);
  font-size: 0.9rem;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.btn-action {
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-primary {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  border: 1px solid var(--action-primary-color);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  border: 1px solid var(--border-color-hover);
}

.btn-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.tactics-content {
  display: grid;
  grid-template-columns: 280px 1fr 320px;
  gap: 20px;
}

@media (max-width: 1600px) {
  .tactics-content {
    grid-template-columns: 260px 1fr 300px;
  }
}

@media (max-width: 1400px) {
  .tactics-content {
    grid-template-columns: 240px 1fr 280px;
  }
}

@media (max-width: 1200px) {
  .tactics-content {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .formation-sidebar {
    order: 2;
  }
  
  .pitch-container {
    order: 1;
  }
  
  .tactics-sidebar {
    order: 3;
  }
}

.formation-sidebar,
.tactics-sidebar {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

@media (max-width: 1200px) {
  .formation-sidebar,
  .tactics-sidebar {
    max-height: none;
  }
}

.sidebar-section {
  margin-bottom: 30px;
}

.sidebar-section:last-child {
  margin-bottom: 0;
}

.sidebar-section h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--heading-color);
  font-size: 1.1rem;
  margin: 0 0 15px 0;
}

.sidebar-section h3 i {
  color: var(--section-icon);
}

.match-type-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.type-btn {
  padding: 12px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: var(--subtext-color);
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  text-align: left;
}

.type-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--border-color-hover);
}

.type-btn.active {
  background: var(--border-color-hover);
  color: var(--text-color1);
  border-color: var(--border-color-hover);
}

.type-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.match-type-note {
  margin-top: 10px;
  font-size: 0.85rem;
  color: var(--subtext-color);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.match-type-note i {
  color: var(--section-icon);
}

.category-tabs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.category-tab {
  padding: 12px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: var(--subtext-color);
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
}

.category-tab:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--border-color-hover);
}

.category-tab.active {
  background: var(--border-color-hover);
  color: var(--text-color1);
  border-color: var(--border-color-hover);
}

.formations-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.formation-btn {
  padding: 12px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: var(--subtext-color);
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  text-align: left;
}

.formation-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--border-color-hover);
  transform: translateX(5px);
}

.formation-btn.active {
  background: var(--border-color-hover);
  color: var(--text-color1);
  border-color: var(--border-color-hover);
}


.pitch-container {
  background: var(--card-bg);
  border-radius: 20px;
  padding: clamp(15px, 2vw, 30px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 1.5vw, 20px);
  position: relative;
  overflow: visible;
  width: 100%;
  max-width: 100%;
}

@media (max-width: 768px) {
  .pitch-container {
    padding: clamp(12px, 1.5vw, 20px);
    border-radius: 15px;
    gap: clamp(8px, 1vw, 15px);
  }
}

@media (max-width: 480px) {
  .pitch-container {
    padding: 10px;
    border-radius: 12px;
    gap: 10px;
  }
}

@media (max-width: 768px) {
  .pitch-container {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .pitch-container {
    padding: 15px;
    border-radius: 15px;
  }
}

.pitch-wrapper {
  position: relative;
  width: 100%;
  max-width: 100%;
  padding-bottom: clamp(60%, 100%, 100%); /* Responsive aspect ratio */
  border-radius: clamp(10px, 2vw, 15px);
  overflow: hidden;
  border: clamp(2px, 0.5vw, 4px) solid rgba(255, 255, 255, 0.5);
  min-height: clamp(400px, 50vh, 700px);
  max-height: clamp(500px, 80vh, 900px);
  box-shadow: 
    0 15px 50px rgba(0, 0, 0, 0.5),
    0 0 0 2px rgba(255, 255, 255, 0.1);
  background: #1a3009; /* Fallback background color */
}

@media (max-width: 1600px) {
  .pitch-wrapper {
    padding-bottom: clamp(65%, 100%, 100%);
    min-height: clamp(450px, 45vh, 650px);
    max-height: clamp(550px, 75vh, 850px);
  }
}

@media (max-width: 1400px) {
  .pitch-wrapper {
    padding-bottom: clamp(70%, 100%, 100%);
    min-height: clamp(400px, 40vh, 600px);
    max-height: clamp(500px, 70vh, 800px);
  }
}

@media (max-width: 1200px) {
  .pitch-wrapper {
    padding-bottom: clamp(75%, 100%, 100%);
    min-height: clamp(400px, 35vh, 550px);
    max-height: clamp(500px, 65vh, 700px);
  }
}

@media (max-width: 992px) {
  .pitch-wrapper {
    padding-bottom: clamp(80%, 100%, 100%);
    min-height: clamp(350px, 30vh, 500px);
    max-height: clamp(450px, 60vh, 650px);
  }
}

@media (max-width: 768px) {
  .pitch-wrapper {
    padding-bottom: clamp(85%, 100%, 100%);
    min-height: clamp(300px, 25vh, 450px);
    max-height: clamp(400px, 55vh, 600px);
    border-width: clamp(2px, 0.4vw, 3px);
  }
}

@media (max-width: 480px) {
  .pitch-wrapper {
    padding-bottom: clamp(90%, 100%, 110%);
    min-height: clamp(250px, 20vh, 400px);
    max-height: clamp(350px, 50vh, 550px);
    border-width: 2px;
    border-radius: 10px;
  }
}

@media (max-width: 360px) {
  .pitch-wrapper {
    padding-bottom: clamp(95%, 100%, 120%);
    min-height: clamp(200px, 18vh, 350px);
    max-height: clamp(300px, 45vh, 500px);
  }
}

.basketball-court {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 2;
}

.pitch-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  z-index: 1;
  user-select: none;
  pointer-events: none;
  transition: transform 0.3s ease;
}

@media (max-width: 768px) {
  .pitch-image {
    object-fit: contain;
  }
}

@media (max-width: 480px) {
  .pitch-image {
    object-fit: contain;
  }
}


.player-position {
  position: absolute;
  width: clamp(40px, 4vw, 60px);
  height: clamp(40px, 4vw, 60px);
  cursor: pointer;
  z-index: 10;
}

@media (max-width: 1600px) {
  .player-position {
    width: clamp(38px, 3.8vw, 58px);
    height: clamp(38px, 3.8vw, 58px);
  }
}

@media (max-width: 1400px) {
  .player-position {
    width: clamp(36px, 3.6vw, 56px);
    height: clamp(36px, 3.6vw, 56px);
  }
}

@media (max-width: 1200px) {
  .player-position {
    width: clamp(34px, 3.4vw, 54px);
    height: clamp(34px, 3.4vw, 54px);
  }
}

@media (max-width: 992px) {
  .player-position {
    width: clamp(32px, 3.2vw, 52px);
    height: clamp(32px, 3.2vw, 52px);
  }
}

@media (max-width: 768px) {
  .player-position {
    width: clamp(30px, 3vw, 50px);
    height: clamp(30px, 3vw, 50px);
  }
}

@media (max-width: 640px) {
  .player-position {
    width: clamp(28px, 2.8vw, 48px);
    height: clamp(28px, 2.8vw, 48px);
  }
}

@media (max-width: 480px) {
  .player-position {
    width: clamp(26px, 2.6vw, 45px);
    height: clamp(26px, 2.6vw, 45px);
  }
}

@media (max-width: 360px) {
  .player-position {
    width: clamp(24px, 2.4vw, 42px);
    height: clamp(24px, 2.4vw, 42px);
  }
}

.player-marker {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: var(--action-primary-bg);
  border: 3px solid var(--action-primary-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: move;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.player-marker:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.player-marker.forward {
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  border-color: #ff4757;
}

.player-marker.midfielder {
  background: linear-gradient(135deg, #4ecdc4, #44a08d);
  border-color: #00d2d3;
}

.player-marker.defender {
  background: linear-gradient(135deg, #95a5a6, #7f8c8d);
  border-color: #636e72;
}

.player-marker.point_guard,
.player-marker.shooting_guard,
.player-marker.wing,
.player-marker.power_forward,
.player-marker.center {
  border-color: #f59e0b;
}

.player-marker.goalkeeper {
  background: linear-gradient(135deg, #feca57, #ff9ff3);
  border-color: #ff6348;
  border-width: 4px;
  box-shadow: 
    0 0 20px rgba(255, 99, 72, 0.5), 
    0 4px 15px rgba(0, 0, 0, 0.4),
    inset 0 0 10px rgba(255, 255, 255, 0.2);
  z-index: 15;
}

.player-marker.goalkeeper:hover {
  box-shadow: 
    0 0 25px rgba(255, 99, 72, 0.7), 
    0 6px 20px rgba(0, 0, 0, 0.5),
    inset 0 0 15px rgba(255, 255, 255, 0.3);
  transform: scale(1.15);
}

/* Special styling for goalkeeper at goal post */
.player-position.goalkeeper-position {
  z-index: 15;
}

.player-position.goalkeeper-position .player-marker {
  border-width: 4px;
}

.player-marker.selected {
  border: 4px solid var(--border-color-hover);
  box-shadow: 0 0 0 4px rgba(58, 134, 255, 0.3);
  transform: scale(1.15);
}

.player-number {
  font-weight: 800;
  font-size: clamp(0.7rem, 1.5vw, 1.2rem);
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  line-height: 1;
}

@media (max-width: 768px) {
  .player-number {
    font-size: clamp(0.65rem, 1.3vw, 1rem);
  }
}

@media (max-width: 480px) {
  .player-number {
    font-size: clamp(0.6rem, 1.2vw, 0.9rem);
  }
}

.player-name-tag {
  position: absolute;
  bottom: -25px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: clamp(3px, 0.5vw, 4px) clamp(6px, 0.8vw, 8px);
  border-radius: 6px;
  font-size: clamp(0.5rem, 1.2vw, 0.7rem);
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  max-width: clamp(60px, 8vw, 80px);
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 768px) {
  .player-name-tag {
    bottom: -20px;
    max-width: clamp(55px, 7vw, 70px);
    font-size: clamp(0.48rem, 1.1vw, 0.65rem);
    padding: clamp(2px, 0.4vw, 3px) clamp(5px, 0.7vw, 6px);
  }
}

@media (max-width: 480px) {
  .player-name-tag {
    bottom: -18px;
    max-width: clamp(50px, 6vw, 60px);
    font-size: clamp(0.45rem, 1vw, 0.55rem);
    padding: 2px 5px;
  }
}

.remove-player {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--rejected-bg);
  border: 2px solid var(--rejected-border);
  color: var(--rejected-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.7rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.player-marker:hover .remove-player {
  opacity: 1;
}

.empty-position {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px dashed rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  font-size: clamp(0.8rem, 2vw, 1rem);
}

.empty-position:hover {
  border-color: var(--border-color-hover);
  background: rgba(255, 255, 255, 0.1);
  color: var(--border-color-hover);
}

.formation-display {
  text-align: center;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 480px) {
  .formation-display {
    padding: 12px;
  }
  
  .formation-value {
    font-size: 1rem;
  }
}

.formation-label {
  color: var(--subtext-color);
  margin-right: 10px;
}

.formation-value {
  color: var(--heading-color);
  font-weight: 700;
  font-size: 1.1rem;
}


/* === SUBSTITUTIONS SECTION === */
.substitutions-section {
  border-top: none;
  padding-top: 0;
  margin-top: 0;
}

.section-header-with-count {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.subs-count {
  font-size: 0.85rem;
  color: var(--heading-color);
  background: linear-gradient(135deg, var(--border-color-hover), #4a9eff);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.substitutions-info {
  margin-bottom: 15px;
}

.subs-note {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: var(--subtext-color);
  margin: 0;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.subs-note i {
  color: var(--section-icon);
}

.bench-players {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 400px;
  overflow-y: auto;
}

.bench-player-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.bench-player-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--border-color-hover);
  transform: translateX(5px);
}

.bench-player-item.selected {
  background: rgba(58, 134, 255, 0.2);
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 2px rgba(58, 134, 255, 0.3);
}

.bench-player-avatar {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--subtext-color);
  font-size: 1.2rem;
  flex-shrink: 0;
}

.bench-player-info {
  flex: 1;
  min-width: 0;
}

.bench-player-name {
  font-weight: 600;
  color: var(--heading-color);
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.bench-player-details {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
}

.bench-player-position {
  color: var(--subtext-color);
}

.bench-player-number {
  color: var(--section-icon);
  font-weight: 600;
}

.swap-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--action-primary-bg);
  border: 1px solid var(--action-primary-color);
  color: var(--action-primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  font-size: 0.9rem;
}

.swap-btn:hover:not(:disabled) {
  background: var(--action-primary-color);
  color: white;
  transform: scale(1.1);
}

.swap-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.empty-bench {
  text-align: center;
  padding: 40px 20px;
  color: var(--subtext-color);
}

.empty-bench i {
  font-size: 2.5rem;
  margin-bottom: 10px;
  display: block;
  opacity: 0.5;
}

.empty-bench p {
  margin: 0;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .tactics-main {
    padding: 15px;
  }
  
  .formation-sidebar,
  .tactics-sidebar {
    padding: 20px;
    border-radius: 15px;
  }
  
  .sidebar-section h3 {
    font-size: 1rem;
  }
  
  .formations-list {
    max-height: 250px;
  }
  
  .bench-players {
    max-height: 300px;
  }
}

@media (max-width: 480px) {
  .tactics-main {
    padding: 10px;
  }
  
  .tactics-container {
    gap: 15px;
  }
  
  .formation-sidebar,
  .tactics-sidebar {
    padding: 15px;
  }
  
  .type-btn,
  .category-tab,
  .formation-btn {
    padding: 10px;
    font-size: 0.9rem;
  }
  
  .bench-player-item {
    padding: 10px;
  }
  
  .bench-player-avatar {
    width: 40px;
    height: 40px;
  }
}
</style>
